import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format, isAfter, subWeeks } from "date-fns";
import { fr, enUS, es } from "date-fns/locale";
import { Eye, EyeOff, Trash2 } from "lucide-react";

interface SupervisionRequestsListProps {
  requests: any[];
  onUpdate: () => void;
}

export const SupervisionRequestsList = ({ requests, onUpdate }: SupervisionRequestsListProps) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [showRevokedRequests, setShowRevokedRequests] = useState(false);
  const { toast } = useToast();
  const { t, i18n } = useTranslation('supervision');

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'es': return es;
      case 'en': return enUS;
      default: return fr;
    }
  };

  const handleRequestResponse = async (requestId: string, status: 'accepted' | 'declined') => {
    try {
      setLoading(requestId);
      
      const { error } = await supabase
        .from("supervision_requests")
        .update({ 
          status, 
          responded_at: new Date().toISOString() 
        })
        .eq("id", requestId);

      if (error) throw error;

      toast({
        title: t('common.success'),
        description: status === 'accepted' ? t('management.success.requestAccepted') : t('management.success.requestDeclined'),
      });

      onUpdate();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: error.message,
      });
    } finally {
      setLoading(null);
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    try {
      setDeleting(requestId);
      
      const { error } = await supabase
        .from("supervision_requests")
        .delete()
        .eq("id", requestId);

      if (error) throw error;

      toast({
        title: t('common.success'),
        description: t('management.success.requestDeleted'),
      });

      onUpdate();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: error.message,
      });
    } finally {
      setDeleting(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: "default" | "secondary" | "destructive" | "outline" } = {
      pending: "outline",
      accepted: "default",
      declined: "destructive",
      revoked: "secondary"
    };

    return (
      <Badge variant={variants[status] || "outline"}>
        {t(`management.trainee.status.${status}`)}
      </Badge>
    );
  };

  const getTraineeName = (request: any) => {
    if (request.profiles?.username) {
      return request.profiles.username;
    }
    return t('management.coach.trainee');
  };

  // Fonction pour vérifier si une demande révoquée doit être masquée
  const shouldHideRevokedRequest = (request: any) => {
    if (request.status !== 'revoked' || !request.responded_at) {
      return false;
    }
    
    const revokedDate = new Date(request.responded_at);
    const twoWeeksAgo = subWeeks(new Date(), 2);
    
    return isAfter(revokedDate, twoWeeksAgo) === false;
  };

  // Filtrer les demandes selon l'état d'affichage
  const filteredRequests = requests.filter(request => {
    if (shouldHideRevokedRequest(request)) {
      return showRevokedRequests;
    }
    return true;
  });

  // Compter les demandes révoquées masquées
  const hiddenRevokedCount = requests.filter(request => 
    shouldHideRevokedRequest(request) && !showRevokedRequests
  ).length;

  if (requests.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-500">
          {t('management.coach.requests.noRequests')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {hiddenRevokedCount > 0 && (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <span className="text-sm text-gray-600">
            {t('management.coach.revokedRequests.hidden', { count: hiddenRevokedCount })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRevokedRequests(!showRevokedRequests)}
            className="flex items-center gap-2"
          >
            {showRevokedRequests ? (
              <>
                <EyeOff className="h-4 w-4" />
                {t('management.coach.revokedRequests.hide')}
              </>
            ) : (
              <>
                <Eye className="h-4 w-4" />
                {t('management.coach.revokedRequests.show')}
              </>
            )}
          </Button>
        </div>
      )}

      {filteredRequests.map((request) => (
        <Card key={request.id}>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg">
                  {getTraineeName(request)}
                </CardTitle>
                <p className="text-sm text-gray-600">
                  {format(new Date(request.requested_at), "PPP", { locale: getDateLocale() })}
                </p>
                {request.status === 'revoked' && request.responded_at && (
                  <p className="text-xs text-gray-500">
                    {t('management.coach.revokedRequests.revokedOn')} {format(new Date(request.responded_at), "PPP", { locale: getDateLocale() })}
                  </p>
                )}
              </div>
              {getStatusBadge(request.status)}
            </div>
          </CardHeader>
          <CardContent>
            {request.message && (
              <p className="text-sm mb-4 p-3 bg-gray-50 rounded">
                {request.message}
              </p>
            )}
            
            {request.status === 'pending' && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleRequestResponse(request.id, 'accepted')}
                  disabled={loading === request.id}
                >
                  {t('management.trainee.actions.accept')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRequestResponse(request.id, 'declined')}
                  disabled={loading === request.id}
                >
                  {t('management.trainee.actions.decline')}
                </Button>
              </div>
            )}

            {(request.status === 'revoked' || request.status === 'declined') && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleDeleteRequest(request.id)}
                disabled={deleting === request.id}
                className="flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {deleting === request.id ? t('management.actions.deleting') : t('management.actions.delete')}
              </Button>
            )}
          </CardContent>
        </Card>
      ))}

      {filteredRequests.length === 0 && hiddenRevokedCount > 0 && (
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            {t('management.coach.revokedRequests.allProcessed')}
          </CardContent>
        </Card>
      )}
    </div>
  );
};